import type { FC } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { theme } from "../../../theme";
import { HorizontalSpacer } from "../../atoms/Spacers";
import { useDeleteGroup } from "../../../graphql/hooks/rolesAndPermissions/useDeleteGroup";
import Alert from "../../molecules/Alert";
import { useState, useEffect } from "react";
import TextButton from "../../atoms/buttons/TextButton";
import type {
  SkynetUserEffectivePermissions,
  SkynetUserGroupPermissions,
} from "../../../models/RolesAndPermissionsModels";
import SecondaryPageTitleSection from "../../molecules/SecondaryPageTitleSection";
import Card from "../../atoms/Card";
import { Col, Row } from "react-grid-system";
import TextField from "../../atoms/inputs/TextField";
import MultilineTextField from "../../atoms/inputs/MultilineTextField";
import BodySmall from "../../atoms/typography/BodySmall";
import { SlideOutHeight } from "../../atoms/SlideOutHeight";
import TableHeader from "../../atoms/TableHeader";
import TableHeaderText from "../../atoms/typography/TableHeaderText";
import TableRow from "../../atoms/TableRow";
import Checkbox from "../../atoms/inputs/Checkbox";
import { useSetGroupAdmin } from "../../../graphql/hooks/rolesAndPermissions/useSetGroupAdmin";
import DeleteModal from "../../molecules/modals/DeleteModal";
import { AddIcon } from "../../atoms/icons/AddIcon";
import AddMemberComponent from "../../organisms/users/AddMemberComponent";
import GroupsPermissionsList from "../../molecules/users/GroupsPermissionsList";
import MemberOverridePanel from "../../molecules/users/MemberOverridePanel";
import Modal from "../../molecules/Modal";
import ActionButton from "../../atoms/buttons/ActionButton";
import Divider from "../../atoms/Divider";
import { useUserGroup } from "../../../graphql/hooks/rolesAndPermissions/useUserGroup";
import { useSetMemberPermissions } from "../../../graphql/hooks/rolesAndPermissions/useSetMemberPermissions";
import IconButton from "../../atoms/buttons/IconButton";
import { FixIconSmall } from "../../atoms/icons/FixIcon";
import LoadingSpinner from "../../atoms/LoadingSpinner";
import { useRemoveMember } from "../../../graphql/hooks/rolesAndPermissions/useRemoveMember";
import { CircleActivityIndicator } from "../../atoms/CircleActivityIndicator";
import {
  AVAILABLE_WRITE_PERMISSIONS,
  checkUserInGroupHasOverrides,
  stripGroupPermissionsTypenames,
} from "../../../utils/permissions";
import { useUpdateGroup } from "../../../graphql/hooks/rolesAndPermissions/useUpdateGroup";
import { usePermissions } from "../../../graphql/hooks/usePermissions";
import { EditIcon } from "../../atoms/icons/EditIcon";
import Tooltip from "../../molecules/Tooltip";
import ToggleButton from "../../atoms/buttons/ToggleButton";

const GroupDetailPage: FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from || "/";
  const { groupId } = useParams<{ groupId: string }>();
  const [errorMessage, setErrorMessage] = useState<string | undefined>(
    undefined,
  );

  const { userGroup, error, loading } = useUserGroup(groupId);
  const { permissions } = usePermissions();
  const canEditGroup =
    permissions?.availableForCurrentUser.writePermissions.includes(
      AVAILABLE_WRITE_PERMISSIONS.InternalAdminUserGroup,
    ) ?? false;
  const [isEditingGroup, setIsEditingGroup] = useState(false);

  const [groupName, setGroupName] = useState(userGroup?.name || "");
  const [groupDescription, setGroupDescription] = useState(
    userGroup?.description || "",
  );
  const [groupPermissions, setGroupPermissions] = useState<
    SkynetUserGroupPermissions | undefined
  >(userGroup?.permissions);

  const [groupAdminSuccessfulSnackbar, setGroupAdminSuccessfulSnackbar] =
    useState<boolean>(false);

  useEffect(() => {
    if (userGroup) {
      setGroupName(userGroup.name);
      setGroupDescription(userGroup.description ?? "");
      setGroupPermissions(
        userGroup.permissions
          ? stripGroupPermissionsTypenames(userGroup.permissions)
          : undefined,
      );
    }
  }, [userGroup]);

  const [addMembersOpen, setAddMembersOpen] = useState(false);
  const [managingMemberId, setManagingMemberId] = useState<string | null>(null);

  //delete group
  const onCompletedDeleteGroup = (res?: any) => {
    if (res?.rolesAndPermissions?.deleteGroup?.success) {
      navigate("/groups");
    } else {
      setErrorMessage(t("errors.groupCouldNotBeDeleted"));
    }
  };
  const onErrorDeleteGroup = (e?: any) => {
    setErrorMessage(e?.message || t("errors.groupCouldNotBeDeleted"));
  };
  const {
    deleteGroup,
    error: deleteError,
    loading: deleteLoading,
  } = useDeleteGroup({
    onCompleted: onCompletedDeleteGroup,
    onError: onErrorDeleteGroup,
  });
  const handleDeleteGroup = async (id: string) => {
    try {
      await deleteGroup({ variables: { input: { groupId: id } } });
    } catch (error) {
      setErrorMessage(error?.toString() || t("errors.groupCouldNotBeDeleted"));
    }
  };

  //group admin
  const onCompletedSetGroupAdmin = (result?: any) => {
    if (result?.rolesAndPermissions?.setGroupAdmin?.success) {
      console.log("Group admin set successfully:", result);
      setGroupAdminSuccessfulSnackbar(true);
      setTimeout(() => setGroupAdminSuccessfulSnackbar(false), 3000);
    } else {
      console.error("Failed to set group admin. Please try again.");
      setErrorMessage(
        result?.errors?.message ??
          "Failed to update group admin. Please try again.",
      );
    }
  };
  const onErrorSetGroupAdmin = (error?: any) => {
    console.error("Error setting group admin:", error);
    setErrorMessage(
      error?.message ?? "Failed to update group admin. Please try again.",
    );
  };
  const { setGroupAdmin, error: groupAdminError } = useSetGroupAdmin({
    onCompleted: onCompletedSetGroupAdmin,
    onError: onErrorSetGroupAdmin,
  });

  const onClickSetGroupAdmin = async (memberId: string, groupId?: string) => {
    if (!groupId) {
      return;
    }
    try {
      await setGroupAdmin({
        variables: {
          input: {
            groupId,
            memberId,
          },
        },
      });
    } catch (err) {
      console.error("Error setting group admin:", err);
    }
  };

  //give member override access
  const onCompletedSetMemberOverride = (result?: any) => {
    if (result?.rolesAndPermissions?.setMemberOverride?.success) {
      console.log("Member override set successfully:", result);
    } else {
      console.error("Failed to set member override. Please try again.");
      setErrorMessage(
        result?.errors?.message ??
          "Failed to update member override. Please try again.",
      );
    }
  };
  const onErrorSetMemberOverride = (error?: any) => {
    console.error("Error setting member override:", error);
    setErrorMessage(
      error?.message ?? "Failed to update member override. Please try again.",
    );
  };
  const {
    setMemberPermissions,
    error: overrideError,
    loading: overrideLoading,
  } = useSetMemberPermissions({
    onCompleted: onCompletedSetMemberOverride,
    onError: onErrorSetMemberOverride,
  });

  const onClickSetMemberOverride = async (
    memberId: string,
    overrides: SkynetUserEffectivePermissions,
  ) => {
    if (!groupId) return;
    try {
      await setMemberPermissions({
        variables: {
          input: {
            groupId,
            memberId,
            permissions: overrides,
          },
        },
      });
      setManagingMemberId(null);
    } catch (err) {
      console.error("Error setting member override:", err);
    }
  };

  //remove member from group
  const {
    removeMember,
    loading: removeMemberLoading,
    error: removeMemberError,
  } = useRemoveMember({
    onCompleted: () => {},
    onError: (err) =>
      setErrorMessage(err?.message ?? "Failed to remove member from group"),
  });
  const onRemoveMember = async (memberId: string) => {
    try {
      await removeMember({
        variables: {
          input: {
            groupId: groupId,
            memberId: memberId,
          },
        },
      });
    } catch (e) {
      setErrorMessage(e?.toString());
    }
  };

  //update group
  const onCompletedUpdateGroup = (res?: {
    rolesAndPermissions: { updateGroup: { success: boolean } };
    errors?: { message?: string };
  }) => {
    if (res?.rolesAndPermissions?.updateGroup?.success) {
      setIsEditingGroup(false);
    } else {
      setErrorMessage(res?.errors?.message ?? "Failed to update group.");
    }
  };
  const onErrorUpdateGroup = (e?: any) => {
    setErrorMessage(e?.message ?? "Failed to update group.");
  };
  const {
    updateGroup,
    error: updateGroupError,
    loading: updateGroupLoading,
  } = useUpdateGroup({
    onCompleted: onCompletedUpdateGroup,
    onError: onErrorUpdateGroup,
  });
  const onClickUpdateGroup = async () => {
    try {
      await updateGroup({
        variables: {
          input: {
            groupId: groupId,
            name: groupName,
            description: groupDescription,
            permissions: groupPermissions,
          },
        },
      });
    } catch (error) {
      setErrorMessage(error?.toString() ?? "Failed to update group.");
    }
  };

  const isDirty =
    groupName !== (userGroup?.name ?? "") ||
    groupDescription !== (userGroup?.description ?? "") ||
    JSON.stringify(groupPermissions) !==
      JSON.stringify(
        userGroup?.permissions
          ? stripGroupPermissionsTypenames(userGroup.permissions)
          : undefined,
      );

  return (
    <div>
      <Row nogutter align="end" justify="between">
        <SecondaryPageTitleSection
          currentTitleKey={userGroup?.name || "users.group"}
          titleKey={userGroup?.name || "users.group"}
          from={from}
        />
        <div style={{ display: "flex", gap: 24, alignItems: "center" }}>
          <Tooltip text={t("users.editGroupMaxPermissions")}>
            <ToggleButton
              iconOn={<EditIcon color={theme.colors.white} />}
              iconOff={<EditIcon color={theme.colors.primary} />}
              onToggle={() => setIsEditingGroup((editing) => !editing)}
              size="medium"
              disabled={!canEditGroup || userGroup?.isSystemGroup}
              isOn={isEditingGroup}
            />
          </Tooltip>
          {groupId && (
            <DeleteModal
              onClickDelete={() => handleDeleteGroup(groupId)}
              deleteButtonType="outlinedButtonMedium"
              title={t("users.removeGroup")}
              loading={deleteLoading}
            />
          )}
          <ActionButton
            title={t("users.saveGroupEdits")}
            color={theme.colors.primary}
            onClick={onClickUpdateGroup}
            loading={updateGroupLoading}
            disabled={!isDirty}
          />
        </div>
      </Row>
      <HorizontalSpacer spacing={2} />
      {loading && !userGroup && <LoadingSpinner />}
      <Card spacing={2}>
        {error && (
          <Alert
            type="error"
            title={t("errors.couldNotFetchGroup")}
            message={errorMessage}
          />
        )}
        {deleteError && (
          <Alert
            type="error"
            title={t("errors.groupCouldNotBeDeleted")}
            message={errorMessage}
          />
        )}
        {removeMemberError && (
          <Alert
            type="error"
            title={t("errors.couldNotRemoveMemberFromGroup")}
            message={errorMessage}
          />
        )}
        {updateGroupError && (
          <Alert
            type="error"
            title={t("errors.couldNotUpdateGroup")}
            message={errorMessage}
          />
        )}
        {groupAdminError && (
          <Alert
            type="error"
            title={t("errors.couldNotUpdateGroupAdmin")}
            message={errorMessage}
          />
        )}
        <HorizontalSpacer spacing={1} />
        <Row>
          <Col lg={6}>
            <TextField
              label={t("random.name")}
              fullWidth
              value={groupName}
              onChange={(v) => setGroupName(v)}
            />
            <HorizontalSpacer spacing={1} />
            <MultilineTextField
              label={t("invoices.description")}
              value={groupDescription}
              onChange={(value) => setGroupDescription(value)}
              rows={3}
            />
          </Col>
          <Col lg={6}>
            {groupPermissions && (
              <GroupsPermissionsList
                userGroupPermissions={groupPermissions}
                editable={userGroup?.isSystemGroup ? false : true}
                onPermissionsChange={setGroupPermissions}
                showMaxPermissions={isEditingGroup}
              />
            )}
          </Col>
        </Row>

        <HorizontalSpacer spacing={2} />
        <Divider />
        <HorizontalSpacer spacing={2} />

        <div>
          <Row
            align="center"
            justify="between"
            nogutter
            style={{ cursor: "pointer" }}
          >
            <BodySmall fontweight={500}>{t("menu.users")}</BodySmall>
            <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
              <BodySmall color={theme.colors.textMuted}>
                {userGroup?.memberCount} {t("users.members")}
              </BodySmall>
              <TextButton
                icon={<AddIcon color={theme.colors.primary} />}
                title={t("users.addUser")}
                onClick={() => setAddMembersOpen(true)}
                color={theme.colors.primary}
                disabled={userGroup?.isSystemGroup}
              />
            </div>
          </Row>
          <SlideOutHeight isVisible={true}>
            <HorizontalSpacer spacing={1} />
            <TableHeader>
              <Col lg={9}>
                <TableHeaderText>{t("random.name")}</TableHeaderText>
              </Col>
              <Col lg={2} style={{ display: "flex", justifyContent: "center" }}>
                <TableHeaderText>{t("users.groupAdminShort")}</TableHeaderText>
              </Col>
              <Col lg={1}></Col>
            </TableHeader>
            {userGroup?.members?.map((user) => (
              <div key={user?.skynetUserId}>
                <TableRow clickable={false} justify="between" align="center">
                  <Col
                    lg={9}
                    style={{ display: "flex", alignItems: "center", gap: 8 }}
                  >
                    <BodySmall>
                      {user?.skynetUser?.firstName} {user?.skynetUser?.lastName}
                    </BodySmall>
                    {user && checkUserInGroupHasOverrides(user) && (
                      <CircleActivityIndicator
                        color={theme.colors.warning}
                        small
                      />
                    )}
                  </Col>
                  <Col
                    lg={2}
                    style={{ display: "flex", justifyContent: "center" }}
                  >
                    <Checkbox
                      checked={user?.isGroupAdmin}
                      onChange={() =>
                        onClickSetGroupAdmin(user?.id, userGroup?.id)
                      }
                      disabled={userGroup?.isSystemGroup}
                    />
                  </Col>
                  <Col
                    lg={1}
                    style={{
                      display: "flex",
                      justifyContent: "flex-end",
                      gap: 16,
                      alignItems: "center",
                    }}
                  >
                    <IconButton
                      icon={<FixIconSmall color={theme.colors.primary} />}
                      onClick={() => setManagingMemberId(user?.id ?? null)}
                      size="medium"
                      color={theme.colors.primaryBg}
                    />
                    <DeleteModal
                      deleteButtonType="iconButtonMedium"
                      onClickDelete={() => onRemoveMember(user?.id)}
                      loading={removeMemberLoading}
                    />
                  </Col>
                </TableRow>
                {managingMemberId === user?.id && (
                  <Modal
                    closeIcon
                    onClose={() => setManagingMemberId(null)}
                    width={800}
                  >
                    {overrideError && (
                      <Alert
                        type="error"
                        title={t("errors.couldNotUpdateUserPermissions")}
                        message={errorMessage}
                      />
                    )}
                    {user && userGroup.permissions && (
                      <MemberOverridePanel
                        member={user}
                        groupPermissions={userGroup.permissions}
                        onSave={(overrides) =>
                          onClickSetMemberOverride(user.id, overrides)
                        }
                        onCancel={() => setManagingMemberId(null)}
                        saving={overrideLoading}
                      />
                    )}
                  </Modal>
                )}
              </div>
            ))}
            <HorizontalSpacer spacing={0.5} />
          </SlideOutHeight>
        </div>

        <div>
          <Row nogutter justify="end"></Row>
          {addMembersOpen && userGroup?.id && (
            <Modal
              onClose={() => {
                setAddMembersOpen(false);
              }}
              width={1200}
              title={t("users.addUser")}
              closeIcon
            >
              <AddMemberComponent
                groupId={userGroup.id}
                onSuccess={() => setAddMembersOpen(false)}
              />
            </Modal>
          )}
        </div>
      </Card>

      {groupAdminSuccessfulSnackbar && (
        <Alert
          type="success"
          title={t("users.groupAdminUpdateSuccessful")}
          version="snackbar"
          onClickClose={() => setGroupAdminSuccessfulSnackbar(false)}
          small
        />
      )}
    </div>
  );
};

export default GroupDetailPage;
